/* eslint-disable react/no-unescaped-entities */
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import {
  MagnifyingGlass,
  CaretDown,
  ArrowRight,
  Car as CarIcon,
  Users as UsersIcon,
  Airplane,
  ChatCircleDots,
  ChartLineUp,
  CheckCircle,
  Phone,
  Warning,
  Trophy,
  Money,
  Handshake,
  Sparkle,
} from "@phosphor-icons/react";

const SECTIONS = [
  {
    icon: Handshake,
    title: "مرحباً بك في فريق مراسم الزوار",
    body: (
      <>
        <p>أهلاً بك سائقاً معتمداً في <b>مراسم الزوار</b> — منصة النقل الفاخر لزوار الحرمين. عملك معنا يعني تجربة استثنائية للعميل ودخلاً منتظماً لك عبر رحلات مضمونة الجدولة والتسعير.</p>
        <ul>
          <li>تتلقى رحلاتك مباشرة عبر التطبيق ورسائل واتساب.</li>
          <li>الأجرة تُضاف تلقائياً لرصيدك بعد إتمام كل رحلة.</li>
          <li>تسدد الإدارة مستحقاتك حسب الاتفاق (أسبوعي/شهري).</li>
        </ul>
      </>
    ),
  },
  {
    icon: CheckCircle,
    title: "تسجيل الدخول",
    body: (
      <>
        <ol>
          <li>افتح تطبيق مراسم الزوار.</li>
          <li>اختر تبويب "دخول السائق".</li>
          <li>أدخل رقم جوالك المسجَّل + رمز الدخول (6 أرقام).</li>
          <li>ستحصل على رمز الدخول من الإدارة عبر واتساب بعد قبول تسجيلك.</li>
        </ol>
        <div className="help-tip">💡 احتفظ برمز الدخول في مكان آمن — لا تشاركه مع أي شخص. للمساعدة تواصل مع الإدارة لإعادة إصداره.</div>
      </>
    ),
  },
  {
    icon: ChartLineUp,
    title: "صفحتي الرئيسية",
    body: (
      <>
        <p>عند تسجيل الدخول ستجد لوحتك الشخصية تعرض:</p>
        <ul>
          <li>💰 <b>الرصيد الحالي</b> — المستحقات غير المسددة لك.</li>
          <li>📊 <b>أرباح الشهر الحالي</b> — إجمالي كسبك خلال هذا الشهر.</li>
          <li>🏆 <b>إجمالي الأرباح</b> — كسبك التراكمي منذ الانضمام.</li>
          <li>📋 <b>قائمة الرحلات</b> — قادمة، حالية، مكتملة، ملغاة.</li>
        </ul>
      </>
    ),
  },
  {
    icon: ChatCircleDots,
    title: "كيف تستقبل الحجوزات",
    body: (
      <>
        <ol>
          <li>الإدارة تُعيّنك على حجز مناسب لنوع سيارتك.</li>
          <li>يصلك <b>إشعار داخل التطبيق</b> فوراً.</li>
          <li>يصلك <b>واتساب</b> بتفاصيل الرحلة كاملة (عميل + وقت + مسار).</li>
          <li>الحجز يظهر في تبويب "قادمة" في لوحتك.</li>
        </ol>
        <div className="help-tip">💡 عند تعارض المواعيد ستظهر لك رسالة تنبيه — تواصل مع الإدارة قبل القبول.</div>
      </>
    ),
  },
  {
    icon: CarIcon,
    title: "بدء الرحلة",
    body: (
      <>
        <ol>
          <li>افتح تفاصيل الحجز من قائمة الرحلات القادمة.</li>
          <li>تواصل مع العميل مسبقاً — اتصال مباشر أو واتساب.</li>
          <li>وصولك للعميل يجب أن يكون قبل الموعد بـ 10 دقائق.</li>
          <li>عند التقاء العميل، اضغط <b>"بدء الرحلة"</b>.</li>
        </ol>
      </>
    ),
  },
  {
    icon: CheckCircle,
    title: "إنهاء الرحلة",
    body: (
      <>
        <ol>
          <li>بعد إيصال العميل إلى الوجهة، اضغط <b>"إنهاء الرحلة"</b>.</li>
          <li>تتغيّر الحالة إلى "مكتمل" في النظام.</li>
          <li>الأجرة المحدَّدة للحجز تُضاف تلقائياً إلى رصيدك.</li>
          <li>تظهر الرحلة في تبويب "مكتملة" مع تفاصيل السعر.</li>
        </ol>
      </>
    ),
  },
  {
    icon: Money,
    title: "ملخص الأرباح",
    body: (
      <>
        <p>يعرض لك النظام تفصيلاً كاملاً لدخلك:</p>
        <ul>
          <li>الأرباح حسب الشهر — رسم بياني وتفصيل.</li>
          <li>الأرباح حسب نوع السيارة — إذا كنت تعمل على أكثر من سيارة.</li>
          <li>الأرباح حسب المسار — أي مسار أكثر ربحية لك.</li>
        </ul>
        <div className="help-tip">💡 <b>الرصيد الحالي</b> = المبالغ التي لم تُسدد بعد. <b>الإجمالي</b> = كل ما كسبته منذ الانضمام (يشمل المسدَّد).</div>
      </>
    ),
  },
  {
    icon: Airplane,
    title: "الرحلات المتعددة",
    body: (
      <>
        <p>بعض العملاء يحجزون رحلة متعددة المسارات (مثال: مكة ← جدة ← المدينة). في هذه الحالة:</p>
        <ul>
          <li>سترى جميع المسارات مرتبة زمنياً.</li>
          <li>كل مسار له وقت مستقل — يجب أن تكون جاهزاً في الوقت المحدد لكل مسار.</li>
          <li>الأجرة تحتسب لكل مسار على حدة وتضاف لرصيدك بعد اكتماله.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Phone,
    title: "التواصل مع العميل",
    body: (
      <>
        <ul>
          <li>استخدم <b>زر الاتصال 📞</b> للمكالمة المباشرة (خصوصاً في الوقت الحرج).</li>
          <li>استخدم <b>زر واتساب 📱</b> للرسائل المكتوبة والتأكيدات.</li>
          <li>كن مهذباً واحترافياً في كل اتصال.</li>
          <li>التزم بالوقت المحدد — التأخير يؤثر على تقييمك.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Warning,
    title: "مشكلات وحلول",
    body: (
      <>
        <dl>
          <dt>العميل لم يجب على الاتصال؟</dt>
          <dd>حاول عبر واتساب. إن استمر عدم الرد بعد 15 دقيقة من الموعد، تواصل مع الإدارة.</dd>
          <dt>العميل تأخر عن الموعد؟</dt>
          <dd>انتظر حتى 20 دقيقة كحد أقصى. أي تأخير أطول يستدعي التواصل مع الإدارة.</dd>
          <dt>عطل في السيارة أثناء الرحلة؟</dt>
          <dd>اتصل بالإدارة فوراً. سيتم إرسال بديل عاجل. لا تترك العميل بمفرده.</dd>
          <dt>الحاجة إلى إلغاء رحلة؟</dt>
          <dd>لا تلغِ من التطبيق مباشرة — اتصل بالإدارة أولاً لأسباب الإلغاء وترتيب البديل.</dd>
        </dl>
      </>
    ),
  },
  {
    icon: Trophy,
    title: "نصائح لسائق ناجح",
    body: (
      <>
        <ul>
          <li>🕐 <b>الالتزام بالوقت</b> — أهم صفة عند العملاء الذين نخدمهم.</li>
          <li>🧼 <b>سيارة نظيفة</b> داخلياً وخارجياً قبل كل رحلة.</li>
          <li>🤝 <b>ترحيب حار</b> بالعميل مع مساعدته في الأمتعة.</li>
          <li>🚗 <b>قيادة آمنة ومريحة</b> — تجنّب المفاجآت والانعطافات الحادة.</li>
          <li>🤫 <b>احترام الخصوصية</b> — لا تُشارك تفاصيل رحلات العملاء مع أي طرف.</li>
        </ul>
        <div className="help-tip">🌟 السائق المتميز يحصل على رحلات أكثر وفرص لجولات كبار الزوار.</div>
      </>
    ),
  },
  {
    icon: UsersIcon,
    title: "التواصل مع الإدارة",
    body: (
      <>
        <ul>
          <li>📞 <b>مدير الحجوزات:</b> تجد رقمه في صفحة "عن الشركة" داخل التطبيق.</li>
          <li>⏰ <b>ساعات العمل:</b> 24/7 لطوارئ الرحلات.</li>
          <li>📅 <b>طلب إجازة:</b> أرسل رسالة للإدارة قبل 24 ساعة على الأقل.</li>
          <li>💰 <b>الاستفسار عن المدفوعات:</b> ملخص الرصيد + السجل يتوفر في لوحتك — للنقاش تواصل مع الإدارة.</li>
        </ul>
      </>
    ),
  },
];

function AccordionItem({ section, defaultOpen = false, matched = false }) {
  const [open, setOpen] = useState(defaultOpen || matched);
  const Icon = section.icon;
  return (
    <div className={`help-section ${matched ? "help-matched" : ""}`}>
      <button
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        data-testid={`drv-help-toggle-${section.title}`}
        className="w-full flex items-center justify-between p-4 text-right"
      >
        <span className="inline-flex items-center gap-2 text-primary font-bold">
          <Icon size={18} weight="duotone" />
          <span>{section.title}</span>
        </span>
        <CaretDown size={16} weight="bold" className={`text-primary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="help-content px-5 pb-5">{section.body}</div>}
    </div>
  );
}

export default function DriverHelp() {
  const { user } = useAuth();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return SECTIONS.map((s) => ({ s, matched: false }));
    return SECTIONS.map((s) => {
      const inTitle = s.title.toLowerCase().includes(query);
      const bodyText = JSON.stringify(s.body).toLowerCase();
      return { s, matched: inTitle || bodyText.includes(query) };
    }).filter((x) => x.matched);
  }, [q]);

  if (user?.role && user.role !== "driver" && user.role !== "admin") {
    return <Navigate to="/help" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/"
            className="w-9 h-9 rounded-full border border-primary/30 text-primary hover:bg-primary/10 inline-flex items-center justify-center"
            data-testid="drv-help-back"
          >
            <ArrowRight size={16} weight="bold" />
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <CarIcon size={26} weight="duotone" className="text-primary" />
              دليل السائق
            </h1>
            <p className="text-white/50 text-sm mt-1">
              كل ما تحتاجه لتقديم تجربة استثنائية لعملاء مراسم الزوار.
            </p>
          </div>
        </div>

        <div className="relative mb-6">
          <MagnifyingGlass size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/60" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث في الدليل…"
            className="w-full pr-11 pl-4 py-3 rounded-xl bg-card border border-primary/25 text-white placeholder:text-white/40 focus:border-primary outline-none"
            data-testid="drv-help-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-white/50 bg-card border border-primary/15 rounded-2xl">
            لا توجد نتائج تطابق "{q}"
          </div>
        ) : (
          <div className="space-y-3" data-testid="drv-help-sections">
            {filtered.map(({ s, matched }, i) => (
              <AccordionItem key={s.title} section={s} defaultOpen={i === 0 && !q} matched={matched && !!q} />
            ))}
          </div>
        )}

        <div className="mt-8 rounded-2xl p-5 bg-primary/8 border border-primary/25 text-center">
          <Sparkle size={22} weight="fill" className="text-primary mx-auto mb-2" />
          <div className="text-white font-bold text-sm">تحتاج مساعدة إضافية؟</div>
          <div className="text-white/60 text-xs mt-1">
            تواصل مع الإدارة مباشرة من <Link to="/about" className="text-primary underline underline-offset-2">صفحة "عن الشركة"</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
